import { useEffect, useState } from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@shared/authContext';
import MessageComposer from '@shared/messaging/MessageComposer';
import MessageThreadView from '@shared/messaging/MessageThreadView';
import ServiceCallLinkPicker from '@shared/messaging/ServiceCallLinkPicker';
import {
  useEnsureMessageThread,
  useMessageThreadConversation,
} from '@shared/messaging/useMessaging';
import WebPushPrompt from '../components/WebPushPrompt';

export default function Messages() {
  const { user } = useAuth();
  const [linkedServiceCallId, setLinkedServiceCallId] = useState(null);
  const [linkedServiceCallLabel, setLinkedServiceCallLabel] = useState(null);

  const { data: thread, isLoading: threadLoading } = useEnsureMessageThread(user?.id, {
    enabled: user?.role === 'technician' && !!user?.id,
  });

  const {
    messages,
    isLoading: messagesLoading,
    isSending,
    sendMessage,
    markRead,
  } = useMessageThreadConversation(thread?.id, {
    enabled: !!thread?.id,
    liveUpdates: true,
  });

  useEffect(() => {
    if (!thread?.id) return;
    markRead().catch(() => {});
  }, [thread?.id, markRead]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type === 'direct_message') {
        window.location.assign('/messages');
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const handleSend = async ({ body, serviceCallId }) => {
    try {
      await sendMessage({ body, serviceCallId });
      if (serviceCallId) {
        setLinkedServiceCallId(null);
        setLinkedServiceCallLabel(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    }
  };

  if (user?.role !== 'technician') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <div className="card-body text-center text-sm text-gray-600">
            Direct messaging is available to field technicians. Admins can message techs from the Hub dispatch workspace.
          </div>
        </div>
      </div>
    );
  }

  const isLoading = threadLoading || (messagesLoading && messages.length === 0);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col min-h-[calc(100dvh-11rem)]">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-100 text-primary-700">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Office Messages</h1>
          <p className="text-sm text-gray-500">Chat with the dispatch office</p>
        </div>
      </div>

      <WebPushPrompt />

      <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm min-h-[24rem]">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading conversation…
          </div>
        ) : (
          <>
            <MessageThreadView
              messages={messages}
              currentUserId={user?.id}
              isLoading={messagesLoading}
              showSenderNames
              emptyMessage="No messages yet. Send a note to the office — they'll see it in the Hub."
            />
            <MessageComposer
              onSend={handleSend}
              isSending={isSending}
              placeholder="Message the office…"
              serviceCallId={linkedServiceCallId}
              serviceCallLabel={linkedServiceCallLabel}
              onClearServiceCall={() => {
                setLinkedServiceCallId(null);
                setLinkedServiceCallLabel(null);
              }}
              linkPicker={(
                <ServiceCallLinkPicker
                  selectedCallId={linkedServiceCallId}
                  selectedCallLabel={linkedServiceCallLabel}
                  onSelect={({ serviceCallId: callId, serviceCallLabel: callLabel }) => {
                    setLinkedServiceCallId(callId);
                    setLinkedServiceCallLabel(callLabel);
                  }}
                  onClear={() => {
                    setLinkedServiceCallId(null);
                    setLinkedServiceCallLabel(null);
                  }}
                />
              )}
            />
          </>
        )}
      </div>
    </div>
  );
}
